import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'

import { composeMessageRouteName } from '@/routes/user/compose'
import { UserSettingsRoutes } from '@/routes/user/settings'
import { AutomationRoutes } from '@/routes/user/automations/types'

import type { Route } from '@/services/route/types'
import type { RedirectResolverServiceResult, Resolvable } from '@/services/resolvers/types'
import type { RedirectParams } from '@/services/resolvers/domain/redirect/types'
import { RedirectErrorCode, RedirectStatus, RedirectType } from '@/services/resolvers/domain/redirect/types'

import type RouterService from '@/services/route/routerService'
import type NotificationService from '@/services/notificationService'
import type TranslateService from '@/services/translateService'
import type ExternalIntegrationsService from '@/services/domain/externalIntegrations/ExternalIntegrationsService'
import type HubspotIntegrationService from '@/services/domain/externalIntegrations/HubspotIntegrationService'
import { ExternalIntegrationProvider } from '@/data/models/domain/externalIntegrations'

const CONNECTION_ERROR_MESSAGE_KEYS: Partial<
  Record<ExternalIntegrationProvider, Partial<Record<RedirectErrorCode, string>>>
> = {
  [ExternalIntegrationProvider.HUBSPOT]: {
    [RedirectErrorCode.STORE_ALREADY_CONNECTED]: 'externalIntegrations.hubspot.setup.errors.storeAlreadyConnected',
  },
  [ExternalIntegrationProvider.ZOHO]: {
    [RedirectErrorCode.STORE_ALREADY_CONNECTED]: 'externalIntegrations.zohocrm.setup.errors.storeAlreadyConnected',
  },
}

@injectable()
export default class RedirectResolverService implements Resolvable<RedirectResolverServiceResult> {
  constructor(
    @inject(SERVICE_TYPES.RouterService)
    protected readonly routerService: RouterService,
    @inject(SERVICE_TYPES.ExternalIntegrationsService)
    protected readonly externalIntegrationsService: ExternalIntegrationsService,
    @inject(SERVICE_TYPES.HubspotIntegrationService)
    protected readonly hubspotIntegrationService: HubspotIntegrationService,
    @inject(SERVICE_TYPES.NotificationService)
    protected readonly notificationService: NotificationService,
    @inject(SERVICE_TYPES.TranslateService)
    protected readonly translateService: TranslateService,
  ) {}

  public getResolverKeys = () => ['redirectResolver']

  public async resolve(_, route: Route) {
    const params = route.query as RedirectParams

    switch (params.type) {
      case RedirectType.SHOPIFY:
        return this.resolveConnectionRedirect(ExternalIntegrationProvider.SHOPIFY, params.status, params.errorCode)
      case RedirectType.WOO_COMMERCE:
        return this.resolveConnectionRedirect(ExternalIntegrationProvider.WOO_COMMERCE, params.status, params.errorCode)
      case RedirectType.HUBSPOT:
        return this.resolveConnectionRedirect(ExternalIntegrationProvider.HUBSPOT, params.status, params.errorCode)
      case RedirectType.ZOHO:
        return this.resolveConnectionRedirect(ExternalIntegrationProvider.ZOHO, params.status, params.errorCode)
      case RedirectType.SHOPIFY_RECONNECTION:
        return this.resolveReconnectionRedirect(ExternalIntegrationProvider.SHOPIFY)
      case RedirectType.WOO_COMMERCE_RECONNECTION:
        return this.resolveReconnectionRedirect(ExternalIntegrationProvider.WOO_COMMERCE)
      case RedirectType.HUBSPOT_RECONNECTION:
        return this.resolveReconnectionRedirect(ExternalIntegrationProvider.HUBSPOT)
      case RedirectType.HUBSPOT_MARKETPLACE_INSTALL:
        return this.resolveHubspotMarketplaceInstallRedirect(params.returnUrl)
      case RedirectType.ZOHO_RECONNECTION:
        return this.resolveReconnectionRedirect(ExternalIntegrationProvider.ZOHO)
      case RedirectType.AUTOMATION_LOGS:
        return this.resolveAutomationLogsRedirect(params.automationId)
      default:
        return this.resolveUnknownRedirect()
    }
  }

  private async resolveConnectionRedirect(
    provider: ExternalIntegrationProvider,
    status?: RedirectStatus,
    errorCode?: RedirectErrorCode,
  ) {
    await this.routerService.replace({
      name: UserSettingsRoutes.IntegrationSetup,
      params: { provider },
    })

    if (status !== RedirectStatus.ERROR) {
      return
    }

    const messageKey = errorCode ? CONNECTION_ERROR_MESSAGE_KEYS[provider]?.[errorCode] : undefined
    const message = this.translateService.t(messageKey ?? 'externalIntegrations.providerConnectionError')
    this.notificationService.notifyError(message)
  }

  private async resolveReconnectionRedirect(provider: ExternalIntegrationProvider) {
    await this.externalIntegrationsService.loadData()

    const integration = this.externalIntegrationsService.findEntityByIdOrNull(provider)

    if (!integration?.connectionId) {
      await this.routerService.replace({ name: UserSettingsRoutes.Integrations })
      const message = this.translateService.t('externalIntegrations.providerReconnectionError')
      this.notificationService.notifyError(message)
      return
    }

    await this.routerService.replace({
      name: UserSettingsRoutes.IntegrationDetails,
      params: { id: String(integration.connectionId) },
    })
  }

  private async resolveHubspotMarketplaceInstallRedirect(
    returnUrl: string,
  ): Promise<RedirectResolverServiceResult | undefined> {
    try {
      const authorizeUrl = await this.hubspotIntegrationService.getMarketplaceAuthorizeUrl(returnUrl)

      return {
        authorizeUrl,
      }
    } catch (error) {
      await this.routerService.replace({ name: UserSettingsRoutes.Integrations })

      this.notificationService.notifyFromError(error)
      return undefined
    }
  }

  private async resolveAutomationLogsRedirect(automationId: string) {
    await this.routerService.replace({
      name: AutomationRoutes.logs,
      state: { automationId },
    })
  }

  private async resolveUnknownRedirect() {
    await this.routerService.replace({ name: composeMessageRouteName })
  }
}
